import React, { useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Flex, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { toDbId } from "~/utils/graphql-helpers";
import { mapStatusToText } from "../../../ComplianceTable/utils";
import { PositionComplianceCaseType } from "../../types";
import UnlinkCaseModal from "../../UnlinkCaseModal";
import {
  CaseLinkIcon,
  PositionCaseStatusContainer,
  CaseStatusLabel,
  textColor,
} from "./styles_vanilla.css";

interface Props {
  caseDetails: PositionComplianceCaseType;
  id: number;
  prospectId: string | null;
  subscriptionId: string | null;
}

const PositionCaseStatus: React.FC<Props> = ({
  caseDetails,
  id,
  prospectId,
  subscriptionId,
}: Props) => {
  const dbProspectId = toDbId(prospectId || "");
  const dbSubscriptionId = toDbId(subscriptionId || "");
  const [isOpen, setIsOpen] = useState(false);
  const { displayText, iconType, iconColor } = mapStatusToText(caseDetails.status || "");

  const openCaseDetailsDrawer = () => {
    window.open(`/canopy/compliance/case/${caseDetails.id}`, "_blank", "noopener,noreferrer");
  };

  const showUnlinkIcon = (): boolean => {
    if (caseDetails.caseType === ComplianceCaseTypeEnum.Remediation) {
      return false;
    }
    if (
      dbProspectId === Number(caseDetails.externalObjectId) ||
      dbSubscriptionId === Number(caseDetails.externalObjectId)
    ) {
      return false;
    }
    return true;
  };

  const handleClick = () => {
    setIsOpen(true);
  };

  return (
    <Flex
      className={PositionCaseStatusContainer}
      alignItems="center"
      justifyContent="space-between"
    >
      <Flex alignItems="center" gap={8}>
        {iconType && <Icon type={iconType} color={iconColor} data-testid="case-status-icon" />}

        <Text
          className={CaseStatusLabel}
          data-testid="case-status-label"
          style={assignInlineVars({
            [textColor]: iconColor,
          })}
          marginBottom="xs"
        >
          {displayText}
        </Text>
      </Flex>

      <Flex alignItems="center" gap={8}>
        {showUnlinkIcon() && (
          <HelpIndicator
            id="unlink-tooltip"
            placement="top"
            content="Unlink case from this position"
          >
            <Icon
              className={`${CaseLinkIcon} hover-icon`}
              type="unlink"
              title="Unlink case from this position"
              color={Colors.Primary600}
              onClick={handleClick}
              data-testid="unlink-icon"
            />
          </HelpIndicator>
        )}
        <HelpIndicator id="case-tooltip" placement="top" content="View case">
          <Icon
            className={`${CaseLinkIcon} hover-icon`}
            type="external-link"
            color={Colors.Primary600}
            data-testid="case-details-icon"
            onClick={openCaseDetailsDrawer}
          />
        </HelpIndicator>
        <UnlinkCaseModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          positionId={String(id)}
        />
      </Flex>
    </Flex>
  );
};

export default PositionCaseStatus;
